import React, { useEffect, useState } from "react";
import DataTable from "../Common/DataTable";
import {
  Tag,
  Input,
  Button,
  Space,
  Modal,
  Upload,
  Form,
  Row,
  Col,
  Typography,
  Grid,
} from "antd";

import {
  PlusOutlined,
  UploadOutlined,
  InboxOutlined,
  EditFilled,
  DeleteFilled,
} from "@ant-design/icons";
import { useAxios } from "../../hooks/useAxios";
import { useNotification } from "../../contexts/NotificationProvider";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css"; // Import the style for the phone input
import SearchInput from "../FormFields/SearchInput";
import useDeleteConfirm from "../../hooks/useDeleteConfirm";

const { Dragger } = Upload;
const { useBreakpoint } = Grid;

const Resources = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [isCSVModalOpen, setIsCSVModalOpen] = useState(false);
  const { axiosInstance } = useAxios();
  const [loading, setLoading] = useState(false);
  const notify = useNotification();
  const [isCreateResrouceModalOpen, setIsCreateResrouceModalOpen] =
    useState(false);
  const [employees, setEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [file, setFile] = useState([]);
  const screens = useBreakpoint();
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const validatePhoneNumber = (rule, value) => {
    if (!value) {
      return Promise.reject("Phone number is required");
    }
    if (!isValidPhoneNumber(value)) {
      return Promise.reject("Please enter a valid phone number");
    }
    return Promise.resolve();
  };

  const uploaderProps = {
    fileList: file,
    onRemove: (prevFile) => {
      const index = file.indexOf(prevFile);
      const newFileList = file.slice();
      newFileList.splice(index, 1);
      setFile(newFileList);
    },
    beforeUpload: (selectedFile) => {
      const isCsvOrXlsx = /\.(csv|xlsx)$/i.test(selectedFile.name);

      if (!isCsvOrXlsx) {
        notify({
          type: "error",
          message: "Invalid File Type",
          description: "Please upload a CSV or XLSX file.",
        });
        setFile([]);
      } else {
        setFile([selectedFile]);
      }
      return false;
    },
  };
  const [form] = Form.useForm();

  const showBulkDeleteConfirm = useDeleteConfirm(async (selectedIds) => {
    setLoading(true);
    try {
      const payload = {
        // Send either the array of IDs or null
        ids: selectedIds,
      };
      await axiosInstance.delete("/api/employees/", {
        data: payload,
      });
      notify({
        type: "success",
        message: selectedIds
          ? `Deleted ${selectedIds.length} employees`
          : "Deleted all employees",
      });
      fetchEmployees();
    } catch (error) {
      notify({
        type: "error",
        message: error.response?.data?.message || "Failed to delete employees",
      });
    } finally {
      setLoading(false);
    }
  });
  const columns = [
    {
      title: "Employee Code",
      dataIndex: "employee_code",
      key: "employee_code",
    },
    {
      title: "Name",
      render: (data) => (
        <span>
          {data?.last_name} {data?.first_name}
        </span>
      ), // Adjust to include avatars if needed
    },
    {
      title: "Contact Number",
      dataIndex: "contact_no",
      key: "contact_no",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={status === "active" ? "green" : "volcano"}>
          {status.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (record) => (
        <Space size="middle">
          <Button
            icon={<EditFilled />}
            onClick={() => handleEdit(record)}
            style={{ border: "none", background: "none", padding: 0 }}
          />
          <Button
            icon={<DeleteFilled />}
            danger
            onClick={() =>
              showBulkDeleteConfirm(
                [record.id],
                "This action will permanently delete the selected resources and their associated rota. Once deleted, this data cannot be recovered."
              )
            }
            style={{ border: "none", background: "none", padding: 0 }}
          />
        </Space>
      ),
    },
  ];

  const showModal = (modalType) => {
    if (modalType === "upload") {
      setIsCSVModalOpen(true);
    } else if (modalType === "create") {
      setSelectedEmployee(null);
      form.resetFields();
      setIsCreateResrouceModalOpen(true);
    }
  };

  const handleUploadCSV = async () => {
    if (!file.length) {
      notify({
        type: "error",
        message: "Error uploading file",
        description:
          "Error uploading file, Please select a valid .csv or .xlsx file",
      });
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append("file", file[0]);
    try {
      const response = await axiosInstance.post(
        "/api/employees/upload/csv/",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      notify({
        type: "success",
        message: response.data?.message,
      });
    } catch (e) {
      setFile([]);
    } finally {
      setLoading(false);
      await fetchEmployees();
      handleCancel();
    }
  };

  const handleCancel = () => {
    setIsCSVModalOpen(false);
    setIsCreateResrouceModalOpen(false);
    setFile([]);
    setSelectedEmployee(null);
    form.resetFields();
  };

  const handleEdit = (record) => {
    setSelectedEmployee(record);
    setIsCreateResrouceModalOpen(true);
    form.setFieldsValue(record);
  };

  const handleUpdateResource = async (formData) => {
    setLoading(true);
    try {
      await axiosInstance.put(
        `/api/employees/${selectedEmployee.id}/`,
        formData
      );
      notify({ type: "success", message: "Employee updated successfully" });
      await fetchEmployees(searchTerm, currentPage);
      setIsCreateResrouceModalOpen(false);
      setSelectedEmployee(null);
      form.resetFields();
    } catch (e) {
      // notify({
      //   type: "error",
      //   message: e.response?.data?.message,
      //   description: e.response?.data?.message,
      // });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateResource = async (formData) => {
    setLoading(true);
    try {
      const response = await axiosInstance.post("/api/employees/", formData);
      setEmployees((prevState) => ({
        ...prevState,
        results: [response.data, ...(prevState?.results || [])],
      }));

      notify({ type: "success", message: "Employee Created Successfully" });
      setIsCreateResrouceModalOpen(false);
      form.resetFields();
    } catch (e) {
      // notify({
      //   type: "error",
      //   message: "Error creating employee",
      //   description: e.response?.data?.message,
      // });
    } finally {
      setLoading(false);
    }
  };
  const handleSubmit = (formData) => {
    if (selectedEmployee) {
      handleUpdateResource(formData);
    } else {
      handleCreateResource(formData);
    }
  };

  const fetchEmployees = async (searchTerm = "", page = 1) => {
    try {
      const params = {
        page,
        ...(searchTerm ? { search: searchTerm } : {}),
      }; // Only include search if not empty

      const response = await axiosInstance.get("/api/employees/", { params });
      setEmployees(response?.data);
    } catch (e) {
      console.log("Error fetching employees", e);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);
  const handleSearch = (searchTerm) => {
    setCurrentPage(1);
    setSearchTerm(searchTerm);
    fetchEmployees(searchTerm, 1);
  };
  useEffect(() => {
    fetchEmployees(searchTerm, currentPage);
  }, [searchTerm, currentPage]);

  return (
    <>
      <Modal
        title="Upload CSV"
        open={isCSVModalOpen}
        onCancel={handleCancel}
        wrapClassName="main-modal-class"
        footer={[
          <Button
            style={{
              backgroundColor: "transparent",
              color: "#324054",
              border: "none",
              borderRadius: "4px",
              padding: "0 16px",
              height: "40px",
              fontWeight: 600,
              width: "118px",
            }}
            variant="text"
            onClick={handleCancel}
          >
            Cancel
          </Button>,
          <Button
            style={{
              backgroundColor: file.length ? "#212180" : "#B0B0B0",
              color: file.length ? "#FFF" : "#8C8C8C",
              border: "none",
              borderRadius: "4px",
              padding: "0 16px",
              height: "40px",
              fontWeight: 600,
              width: "118px",
            }}
            loading={loading}
            variant="text"
            onClick={handleUploadCSV}
            disabled={!file.length || loading}
          >
            Submit
          </Button>,
        ]}
        className="upload-csv-modal"
      >
        <Dragger
          disabled={file.length === 2}
          name="file"
          multiple={false}
          maxCount={1}
          accept=".csv, .xlsx"
          {...uploaderProps}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined fill="#000000" />
          </p>
          <p className="ant-upload-text">
            Upload CSV File here.
            <span
              style={{
                color: "#1677ff",
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              Browse
            </span>
          </p>
        </Dragger>
        <div style={{ marginTop: "16px", textAlign: "center" }}>
          <Typography.Text>Download template file: </Typography.Text>
          <a
            href="/employee_record_template.xlsx" // Place the sample file in the public folder
            download="employee_record_template.xlsx"
            style={{
              color: "#1677ff",
              cursor: "pointer",
              textDecoration: "underline",
              marginLeft: "5px",
            }}
          >
            employee_record_template.xlsx
          </a>
        </div>
      </Modal>

      <Modal
        title={selectedEmployee ? "Edit Resource" : "Create Resource"}
        open={isCreateResrouceModalOpen}
        onCancel={handleCancel}
        wrapClassName="main-modal-class"
        footer={[
          <Button
            style={{
              backgroundColor: "transparent",
              color: "#324054",
              border: "none",
              borderRadius: "4px",
              padding: "0 16px",
              height: "40px",
              fontWeight: 600,
              width: "118px",
            }}
            variant="text"
            onClick={handleCancel}
          >
            Cancel
          </Button>,
          <Button
            style={{
              backgroundColor: "#212180",
              color: "#FFF",
              border: "none",
              borderRadius: "4px",
              padding: "0 16px",
              height: "40px",
              fontWeight: 600,
              width: "118px",
            }}
            variant="text"
            loading={loading}
            disabled={loading}
            onClick={() => form.submit()}
          >
            {selectedEmployee ? "Update" : "Create"}
          </Button>,
        ]}
        className="upload-csv-modal"
      >
        <Form
          form={form}
          layout="vertical"
          name="employeeForm"
          requiredMark={false}
          onFinish={handleSubmit}
        >
          <Form.Item
            validateTrigger="onBlur"
            label="Employee Code"
            name="employee_code"
            rules={[
              { required: true, message: "Employee Code is required" },
              {
                pattern: /^[a-zA-Z0-9-]+$/,
                message: "Employee code is not valid",
              },
              {
                min: 3,
                max: 7,
                message: "Employee code must be between 3 and 7 characters",
              },
            ]}
          >
            <Input
              style={{ background: "#ffffff" }}
              maxLength={7}
              minLength={3}
              disabled={!!selectedEmployee}
            />
          </Form.Item>
          <Row gutter="24">
            <Col span={12}>
              <Form.Item
                validateTrigger="onBlur"
                label="First Name"
                name="first_name"
                rules={[{ required: true, message: "First Name is required" }]}
              >
                <Input style={{ background: "#ffffff" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                validateTrigger="onBlur"
                label="Last Name"
                name="last_name"
                rules={[{ required: true, message: "Last Name is required" }]}
              >
                <Input style={{ background: "#ffffff" }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            validateTrigger="onBlur"
            label="Email"
            name="email"
            rules={[
              {
                type: "email",
                message: "The input is not valid E-mail!",
              },
              {
                required: true,
                message: "Please enter E-mail!",
              },
            ]}
          >
            <Input style={{ background: "#ffffff" }} />
          </Form.Item>

          <Form.Item
            name="contact_no"
            label="Phone Number"
            validateTrigger="onBlur"
            // rules={[
            //   {
            //     validator: validatePhoneNumber,
            //   },
            // ]}
          >
            <PhoneInput
              international={false}
              defaultCountry="GB"
              countries={["GB"]}
              placeholder="Enter phone number"
              style={{
                width: "100%",
                padding: "10px 12px", // Matches Ant Design Input padding
                borderRadius: "4px", // Matches Ant Design border-radius
                borderColor: "#d9d9d9", // Ant Design input border color
                boxSizing: "border-box",
              }}
            />
          </Form.Item>

          {/* <Form.Item validateTrigger="onBlur" label="Status" name="status">
            <Select>
              <Option value="active">Active</Option>
              <Option value="archived">Archived</Option>
            </Select>
          </Form.Item> */}
        </Form>
      </Modal>
      <Row
        gutter={[16, 16]}
        align="middle"
        justify="space-between"
        style={{ marginBottom: 16 }}
      >
        <Col xs={24} sm={24} md={12} lg={6}>
          <SearchInput
            onSearch={handleSearch}
            placeholder="Search by name, email or id ..."
            style={{ width: "100%" }}
          />
        </Col>

        <Col xs={24} sm={24} md={24} lg={12} style={{ textAlign: "right" }}>
          <Space
            style={{ width: "100%", justifyContent: "flex-end" }}
            direction={screens.md ? "horizontal" : "vertical"}
            size="middle"
          >
            <Button
              variant="outlined"
              icon={<PlusOutlined />}
              style={{ width: "190px", height: "40px", padding: "12px 15px" }}
              onClick={() => showModal("create")}
            >
              Create Resource
            </Button>

            <Button
              style={{
                background: "#212180",
                color: "#fff",
                width: "190px",
                height: "40px",
                padding: "12px 15px",
              }}
              onClick={() => showModal("upload")}
              icon={<UploadOutlined />}
            >
              Upload CSV
            </Button>
          </Space>
        </Col>
      </Row>
      <div style={{ background: "#ffffff", borderRadius: "6px" }}>
        <DataTable
          columns={columns}
          rows={employees?.results}
          currentPage={currentPage}
          setCurrentPage={(page) => {
            setCurrentPage(page);
          }}
          position="end"
          marginTop="16px"
          marginBottom="16px"
          pageSize={10}
          totalResults={employees?.count || 0}
          showSizeChanger={false}
          selectable={true}
          rowKey="id"
          onDeleteSelected={showBulkDeleteConfirm}
          deleteAllText="Delete All Resources"
          deleteSelectedText="Delete Selected"
          entities="This action will permanently delete the selected resources and their associated rota. Once deleted, this data cannot be recovered."
        />
      </div>
    </>
  );
};

export default React.memo(Resources);
